import React, { useState, useEffect, useContext, useCallback } from 'react';

import MainLayout from '../../components/layouts/MainLayout';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Avatar, 
  Chip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Divider, 
  MenuItem, 
  Select, 
  InputLabel, 
  FormControl, 
  Checkbox, 
  FormControlLabel,
  TextField,
  InputAdornment,
  Menu,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import ShieldIcon from '@mui/icons-material/Shield';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import AddUserModal from '../../components/main/AddUserModal';
import EditUserModal from '../../components/main/EditUserModal';
import DeleteUserModal from '../../components/main/DeleteUserModal';
import ViewUserModal from '../../components/main/ViewUserModal';
import TrustScoreModal from '../../components/main/TrustScoreModal';
import { CONTEXTS, CONTROLLERS, SERVICES } from 'figshelf-modules';
import UserListTablePagination from '../../components/layouts/data/UserListTablePagination';


const UserManagement = () => {
  const api = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);
  
  // Modal states
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openTrustScoreModal, setOpenTrustScoreModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Data states
  const [userContent, setUserContent] = useState({ data: [], meta: { totalItems: 0, page: 1, pageSize: 10, totalPages: 1 } });
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Filter states
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState(false);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const adminController = new CONTROLLERS.AdminController(api);

      const cacheParams = {
        mode: searchMode && searchQuery ? 'search' : 'list',
        searchQuery: searchMode && searchQuery ? searchQuery : undefined,
        skip: (page - 1) * rowsPerPage,
        take: rowsPerPage,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
      };

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'users',
        params: cacheParams,
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 2 * 60 * 1000 });
      if (cached?.userContent && Array.isArray(cached.userContent?.data)) {
        setUserContent(cached.userContent);
        setTotalItems(cached.totalItems || 0);
      }
      
      if (searchMode && searchQuery) {
        const res = await adminController.searchUsers(searchQuery, (page - 1) * rowsPerPage, rowsPerPage);
        setUserContent(res);
        setTotalItems(res.meta?.total || 0);

        await SERVICES.cacheAdminList(
          cacheKey,
          { userContent: res, totalItems: res.meta?.total || 0 },
          { scope: 'users' },
        );
      } else {
        const params = {
          skip: (page - 1) * rowsPerPage,
          take: rowsPerPage
        };
        
        if (roleFilter) params.role = roleFilter;
        if (statusFilter) params.status = statusFilter;
        
        const res = await adminController.getUsers(params);
        setUserContent(res);
        setTotalItems(res.meta?.totalItems || 0);

        await SERVICES.cacheAdminList(
          cacheKey,
          { userContent: res, totalItems: res.meta?.totalItems || 0 },
          { scope: 'users' },
        );
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({ open: true, message: 'Failed to fetch users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [api, auth?.user?.id, searchMode, searchQuery, page, rowsPerPage, roleFilter, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const adminController = new CONTROLLERS.AdminController(api);

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'user_stats',
        params: { v: 1 },
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 2 * 60 * 1000 });
      if (cached?.stats) {
        setStats(cached.stats);
      }

      const statsData = await adminController.getUserStats();
      setStats(statsData);

      await SERVICES.cacheAdminList(cacheKey, { stats: statsData }, { scope: 'user_stats' });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [api, auth?.user?.id]);

  const handlePageChange = (event, newPage) => {
    console.log(newPage)
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleFilterChange = () => {
    setPage(1);
    setSearchMode(false);
    setSearchQuery('');
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setSearchMode(true);
      setPage(1);
      setRoleFilter('');
      setStatusFilter('');
    }
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setSearchMode(false);
    setPage(1);
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setOpenViewModal(true);
    handleMenuClose();
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setOpenEditModal(true);
    handleMenuClose();
  };

  const handleEditTrustScore = (user) => {
    setSelectedUser(user);
    setOpenTrustScoreModal(true);
    handleMenuClose();
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setOpenDeleteModal(true);
    handleMenuClose();
  };

  const handleSuspendUser = async (user) => {
    try {
      const adminController = new CONTROLLERS.AdminController(api);
      await adminController.suspendUser(user.id);
      setSnackbar({ open: true, message: 'User suspended successfully', severity: 'success' });
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error suspending user:', error);
      setSnackbar({ open: true, message: 'Failed to suspend user', severity: 'error' });
    }
    handleMenuClose();
  };

  const handleActivateUser = async (user) => {
    try {
      const adminController = new CONTROLLERS.AdminController(api);
      await adminController.activateUser(user.id);
      setSnackbar({ open: true, message: 'User activated successfully', severity: 'success' });
      fetchUsers();
      fetchStats();
    } catch (error) {
      console.error('Error activating user:', error);
      setSnackbar({ open: true, message: 'Failed to activate user', severity: 'error' });
    }
    handleMenuClose();
  };

  const handleUserCreated = () => {
    setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
    fetchUsers();
    fetchStats();
  };

  const handleUserUpdated = () => {
    setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
    fetchUsers();
    fetchStats();
  };

  const handleUserDeleted = () => {
    setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
    fetchUsers();
    fetchStats();
  };

  const handleTrustScoreUpdated = () => {
    setSnackbar({ open: true, message: 'Trust score updated successfully', severity: 'success' });
    fetchUsers();
  };

  const handleTogglePremium = async (user) => {
    try {
      const adminController = new CONTROLLERS.AdminController(api);
      if (user.isPremium) {
        await adminController.deactivatePremium(user.id);
        setSnackbar({ open: true, message: `Premium deactivated for @${user.username}`, severity: 'success' });
      } else {
        await adminController.activatePremium(user.id);
        setSnackbar({ open: true, message: `Premium activated for @${user.username}`, severity: 'success' });
      }
      fetchUsers();
    } catch (error) {
      console.error('Error toggling premium:', error);
      setSnackbar({ open: true, message: 'Failed to update premium status', severity: 'error' });
    }
    handleMenuClose();
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statsData = stats ? [
    {
      label: 'Total Users',
      value: stats.totalUsers?.toLocaleString() || '0',
      icon: <PeopleIcon color="primary" />, 
      change: `${stats.percentageChanges?.totalUsers >= 0 ? '+' : ''}${stats.percentageChanges?.totalUsers || 0}%`,
      changeText: 'from yesterday',
      changeColor: stats.percentageChanges?.totalUsers >= 0 ? 'success.main' : 'error.main',
    },
    {
      label: 'Active Users',
      value: stats.activeUsers?.toLocaleString() || '0',
      icon: <CheckCircleIcon color="success" />, 
      change: `${stats.percentageChanges?.activeUsers >= 0 ? '+' : ''}${stats.percentageChanges?.activeUsers || 0}%`,
      changeText: 'from last week',
      changeColor: stats.percentageChanges?.activeUsers >= 0 ? 'success.main' : 'error.main',
    },
    {
      label: 'Suspended Users',
      value: stats.suspendedUsers?.toLocaleString() || '0',
      icon: <CancelIcon color="error" />, 
      change: `${stats.percentageChanges?.suspendedUsers >= 0 ? '+' : ''}${stats.percentageChanges?.suspendedUsers || 0}%`,
      changeText: 'from yesterday',
      changeColor: stats.percentageChanges?.suspendedUsers >= 0 ? 'error.main' : 'success.main',
    },
    {
      label: 'New Today',
      value: stats.newUsersToday?.toLocaleString() || '0',
      icon: <CalendarTodayIcon color="action" />, 
      change: `${stats.percentageChanges?.newUsersToday >= 0 ? '+' : ''}${stats.percentageChanges?.newUsersToday || 0}%`,
      changeText: 'from yesterday',
      changeColor: stats.percentageChanges?.newUsersToday >= 0 ? 'success.main' : 'error.main',
    },
  ] : [];

  const roleStatsData = stats ? [
    {
      label: 'Admins',
      value: stats.adminUsers?.toLocaleString() || '0',
      color: '#d32f2f',
      percentage: stats.totalUsers > 0 ? ((stats.adminUsers / stats.totalUsers) * 100).toFixed(1) : 0,
    },
    {
      label: 'Moderators',
      value: stats.moderatorUsers?.toLocaleString() || '0',
      color: '#ed6c02',
      percentage: stats.totalUsers > 0 ? ((stats.moderatorUsers / stats.totalUsers) * 100).toFixed(1) : 0,
    },
    {
      label: 'Regular Users',
      value: stats.regularUsers?.toLocaleString() || '0',
      color: '#2e7d32',
      percentage: stats.totalUsers > 0 ? ((stats.regularUsers / stats.totalUsers) * 100).toFixed(1) : 0,
    },
  ] : [];

  return (
    <MainLayout>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>User Management</Typography>
        <Typography variant="subtitle2" color="text.secondary" mb={3}>
          Manage and monitor all users in the system
        </Typography>
        
        {/* Statistics Cards */}
        <Grid container spacing={2} mb={3}>
          {statsLoading && !stats ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Grid>
          ) : (
            statsData.map((stat, i) => (
              <Grid item xs={12} sm={6} md={3} key={stat.label}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ bgcolor: 'background.paper', mr: 2 }}>{stat.icon}</Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>{stat.value}</Typography>
                        <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: stat.changeColor, fontWeight: 500 }}>
                      {stat.change} {stat.changeText}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Role-Based Statistics */}
        {!statsLoading && roleStatsData.length > 0 && (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600} mb={2}>Users by Role</Typography>
            </Grid>
            {roleStatsData.map((roleStat) => (
              <Grid item xs={12} sm={4} key={roleStat.label}>
                <Card sx={{ borderRadius: 3, borderLeft: `4px solid ${roleStat.color}` }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h5" fontWeight={700} sx={{ color: roleStat.color }}>
                          {roleStat.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {roleStat.label}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${roleStat.percentage}%`} 
                        size="small" 
                        sx={{ 
                          bgcolor: `${roleStat.color}20`,
                          color: roleStat.color,
                          fontWeight: 600 
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Search and Add User Button */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search users by username, email, or display name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Button size="small" onClick={handleSearchClear}>Clear</Button>
                  </InputAdornment>
                )
              }}
              sx={{ bgcolor: 'white', borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
              sx={{ borderRadius: 2 }}
            >
              Search
            </Button>
            <Button 
              variant="contained" 
              fullWidth 
              onClick={() => setOpenAddModal(true)}
              sx={{ borderRadius: 2 }}
            >
              Add User
            </Button>
          </Grid>
        </Grid>

        {/* Filters */}
        {!searchMode && (
          <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button startIcon={<FilterListIcon />} variant="outlined" sx={{ borderRadius: 2 }}>Filter By</Button>
     
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select 
                label="Role" 
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  handleFilterChange();
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="moderator">Moderator</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select 
                label="Status" 
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  handleFilterChange();
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="blocked">Suspended</MenuItem>
              </Select>
            </FormControl>
            <Button 
              variant="text" 
              color="info" 
              sx={{ ml: 'auto' }}
              onClick={() => {
                setRoleFilter('');
                setStatusFilter('');
                setPage(1);
              }}
            >
              Reset Filters
            </Button>
          </Box>
        )}

        {searchMode && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              label={`Search results for: "${searchQuery}"`} 
              onDelete={handleSearchClear}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
        {/* User Table */}
        <UserListTablePagination 
          list={userContent.data} 
          totalItems={totalItems}
          rowsPerPage={rowsPerPage}
          page={page} 
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
          loading={loading}
          onMenuOpen={handleMenuOpen}
        />

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleViewUser(menuUser)}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            View Details
          </MenuItem>
          <MenuItem onClick={() => handleEditUser(menuUser)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit User
          </MenuItem>
          <MenuItem onClick={() => handleEditTrustScore(menuUser)}>
            <ShieldIcon fontSize="small" sx={{ mr: 1 }} />
            Trust Score
          </MenuItem>
          <MenuItem onClick={() => handleTogglePremium(menuUser)}>
            <WorkspacePremiumIcon fontSize="small" sx={{ mr: 1, color: menuUser?.isPremium ? 'warning.main' : 'text.secondary' }} />
            {menuUser?.isPremium ? 'Remove Premium' : 'Grant Premium'}
          </MenuItem>
          <Divider />
          {menuUser?.status === 'active' ? (
            <MenuItem onClick={() => handleSuspendUser(menuUser)}>
              <BlockIcon fontSize="small" sx={{ mr: 1 }} />
              Suspend User
            </MenuItem>
          ) : (
            <MenuItem onClick={() => handleActivateUser(menuUser)}>
              <CheckIcon fontSize="small" sx={{ mr: 1 }} />
              Activate User
            </MenuItem>
          )}
          <MenuItem onClick={() => handleDeleteUser(menuUser)} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete User
          </MenuItem>
        </Menu>

        {/* Modals */}
        <AddUserModal 
          open={openAddModal} 
          onClose={() => setOpenAddModal(false)} 
          onUserCreated={handleUserCreated}
        />
        <EditUserModal 
          open={openEditModal} 
          onClose={() => setOpenEditModal(false)} 
          user={selectedUser}
          onUserUpdated={handleUserUpdated}
        />
        <DeleteUserModal 
          open={openDeleteModal} 
          onClose={() => setOpenDeleteModal(false)} 
          user={selectedUser}
          onUserDeleted={handleUserDeleted}
        />
        <ViewUserModal 
          open={openViewModal} 
          onClose={() => setOpenViewModal(false)} 
          user={selectedUser}
        />
        <TrustScoreModal
          open={openTrustScoreModal}
          onClose={() => setOpenTrustScoreModal(false)}
          user={selectedUser}
          onUpdated={handleTrustScoreUpdated}
        />

        {/* Snackbar */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false})} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default UserManagement; 